import { useEffect, useRef, useState } from 'react'
import { useUploadEraReportInBatchMutation } from 'store/services/billingCharges'

import { BulkEraUploadItem } from './eraUpload'

const describeUploadError = (error: unknown): string => {
  const message = (error as { data?: { message?: string } })?.data?.message

  return message || 'The upload failed. Try this file again.'
}

/**
 * Uploads ERA files one after another through the single-file endpoint, so
 * each request stays small and quick, one bad file never stops the rest, and
 * every file reports as it lands. One at a time on purpose: a payment that
 * two files of the batch both carry is then caught as a duplicate of the
 * earlier file rather than racing it.
 */
export const useBulkEraUpload = () => {
  const [uploadFile] = useUploadEraReportInBatchMutation()
  const [items, setItems] = useState<BulkEraUploadItem[]>([])
  const [running, setRunning] = useState(false)
  const stopRequested = useRef(false)
  const batch = useRef(0)

  // Leaving the page mid-batch drops the files not yet sent; say so first.
  useEffect(() => {
    if (!running) return undefined

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', warn)

    return () => window.removeEventListener('beforeunload', warn)
  }, [running])

  const update = (key: string, patch: Partial<BulkEraUploadItem>) =>
    setItems((prev) => prev.map((item) => (item.key === key ? { ...item, ...patch } : item)))

  /** Uploads every file and resolves with how each one went. */
  const start = async (files: File[]): Promise<BulkEraUploadItem[]> => {
    batch.current += 1
    stopRequested.current = false

    const results: BulkEraUploadItem[] = files.map((file, index) => ({
      key: `${batch.current}-${index}`,
      filename: file.name,
      state: 'waiting',
    }))

    setItems(results)
    setRunning(true)

    for (const [index, file] of files.entries()) {
      const { key } = results[index]

      if (stopRequested.current) {
        results[index] = { ...results[index], state: 'stopped' }
        update(key, { state: 'stopped' })
        continue
      }

      update(key, { state: 'uploading' })

      try {
        const summary = await uploadFile(file).unwrap()

        results[index] = { ...results[index], state: 'done', summary }
      } catch (error) {
        results[index] = { ...results[index], state: 'failed', error: describeUploadError(error) }
      }

      update(key, results[index])
    }

    setRunning(false)

    return results
  }

  /** Lets the file being uploaded finish, and sends none of the rest. */
  const stop = () => {
    stopRequested.current = true
  }

  return { items, running, start, stop }
}
